import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { financeApi } from '@/services/finance.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

interface DraftLine {
  accountId: string;
  debit: string;
  credit: string;
}

/** A journal entry must balance — enforced here before submit, and again, authoritatively, by the aggregate. */
export function PostJournalEntryDialog({
  onClose,
  onPosted,
}: {
  onClose: () => void;
  onPosted: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { accountId: '', debit: '', credit: '' },
    { accountId: '', debit: '', credit: '' },
  ]);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const accountsQuery = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts(),
  });
  const accounts = accountsQuery.data ?? [];

  const totalDebit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
  const balanced = totalDebit > 0 && totalDebit === totalCredit;
  const linesFilled = lines.every((line) => line.accountId);

  const mutation = useMutation({
    mutationFn: () =>
      financeApi.postJournalEntry({
        description,
        lines: lines.map((line) => ({
          accountId: line.accountId,
          debit: Math.round((Number(line.debit) || 0) * 100),
          credit: Math.round((Number(line.credit) || 0) * 100),
        })),
      }),
    onSuccess: onPosted,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  function updateLine(index: number, patch: Partial<DraftLine>): void {
    setLines((previous) => previous.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="post-entry-title">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="post-entry-title">{t('finance.journalEntry.post')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('finance.journalEntry.description')}
          name="description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          required
          autoFocus
        />

        <p className="field-label">{t('finance.journalEntry.lines')}</p>
        {lines.map((line, index) => (
          <div className="card-fields-row" key={index}>
            <div className="field">
              <label className="field-label" htmlFor={`line-account-${index}`}>
                {t('finance.journalEntry.account')}
              </label>
              <select
                id={`line-account-${index}`}
                className="field-input"
                value={line.accountId}
                onChange={(event) => updateLine(index, { accountId: event.target.value })}
              >
                <option value="" />
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} — {account.name}
                  </option>
                ))}
              </select>
            </div>
            <Field
              label={t('finance.journalEntry.debit')}
              name={`debit-${index}`}
              type="number"
              step="0.01"
              min="0"
              value={line.debit}
              onChange={(event) => updateLine(index, { debit: event.target.value, credit: '' })}
              dir="ltr"
            />
            <Field
              label={t('finance.journalEntry.credit')}
              name={`credit-${index}`}
              type="number"
              step="0.01"
              min="0"
              value={line.credit}
              onChange={(event) => updateLine(index, { credit: event.target.value, debit: '' })}
              dir="ltr"
            />
          </div>
        ))}

        <Button
          type="button"
          variant="ghost"
          onClick={() => setLines((p) => [...p, { accountId: '', debit: '', credit: '' }])}
        >
          {t('finance.journalEntry.addLine')}
        </Button>

        {!balanced && (totalDebit > 0 || totalCredit > 0) && (
          <Alert tone="warning">{t('finance.journalEntry.totalsMustMatch')}</Alert>
        )}

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!description.trim() || !linesFilled || !balanced}
            loading={mutation.isPending}
          >
            {t('finance.journalEntry.post')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
